/**
 * Capture device/browser info at a point in time (e.g. when user starts analysis from landing).
 * Used for live testing and analytics to know which device was used for the skin analysis.
 */

/**
 * @returns {{
 *   user_agent: string,
 *   platform: string,
 *   language: string,
 *   screen_width: number,
 *   screen_height: number,
 *   device_type: 'mobile'|'tablet'|'desktop',
 *   captured_at: string (ISO UTC),
 *   captured_at_local: string (local ISO with offset),
 *   captured_at_epoch_ms: number,
 *   captured_timezone: string,
 *   captured_timezone_offset_minutes: number
 * }}
 */
export function captureDeviceInfo() {
  const now = new Date();
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const pad2 = (n) => String(n).padStart(2, '0');
  const offsetHH = pad2(Math.floor(absOffset / 60));
  const offsetMM = pad2(absOffset % 60);
  const localIsoWithOffset =
    `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}` +
    `T${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}.${String(now.getMilliseconds()).padStart(3, '0')}` +
    `${sign}${offsetHH}:${offsetMM}`;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const platform = typeof navigator !== 'undefined' ? (navigator.platform || navigator.userAgentData?.platform || '') : '';
  const language = typeof navigator !== 'undefined' ? (navigator.language || '') : '';
  const scr = typeof window !== 'undefined' && window.screen;
  const w = scr ? scr.width : 0;
  const h = scr ? scr.height : 0;

  let device_type = 'desktop';
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(max-width: 767px)').matches) device_type = 'mobile';
    else if (
      window.matchMedia('(max-width: 1024px)').matches ||
      window.matchMedia('(max-width: 1366px) and (pointer: coarse) and (hover: none)').matches
    ) {
      device_type = 'tablet';
    }
  }
  // Fallback from userAgent if matchMedia not available
  if (device_type === 'desktop' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua))
    device_type = /iPad|Tablet|Android(?!.*Mobile)/i.test(ua) ? 'tablet' : 'mobile';

  return {
    user_agent: ua,
    platform: String(platform),
    language: String(language),
    screen_width: Number(w) || 0,
    screen_height: Number(h) || 0,
    device_type,
    captured_at: now.toISOString(),
    captured_at_local: localIsoWithOffset,
    captured_at_epoch_ms: now.getTime(),
    captured_timezone:
      (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
    captured_timezone_offset_minutes: offsetMinutes,
  };
}
